import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Search, Loader2, Users, FileText, BookOpen, Megaphone, Mail, Phone, ExternalLink, UserX,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const DEBOUNCE_MS = 250;

function useDebounced(value, delay = DEBOUNCE_MS) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

export default function GlobalSearch() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const debouncedQ = useDebounced(q);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const reqIdRef = useRef(0);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (debouncedQ.trim().length < 2) {
      setResults(null);
      return;
    }
    const myReq = ++reqIdRef.current;
    setLoading(true);
    api
      .get(`/search?q=${encodeURIComponent(debouncedQ)}`)
      .then(({ data }) => {
        if (reqIdRef.current !== myReq) return;
        setResults(data);
      })
      .catch(() => {
        if (reqIdRef.current !== myReq) return;
        setResults({ people: { connected: false, results: [] }, documents: [], sermon_notes: [], announcements: [] });
      })
      .finally(() => {
        if (reqIdRef.current === myReq) setLoading(false);
      });
  }, [debouncedQ]);

  // Close the dropdown on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const close = () => {
    setOpen(false);
    setQ("");
  };

  const goto = (href) => {
    close();
    if (href?.startsWith("/api/")) {
      // Backend asset (document/sermon note) — open in a new tab.
      window.open(`${BACKEND}${href}`, "_blank", "noreferrer");
    } else if (href?.startsWith("http")) {
      window.open(href, "_blank", "noreferrer");
    } else if (href) {
      navigate(href);
    }
  };

  const totalHits =
    (results?.people?.results?.length || 0) +
    (results?.documents?.length || 0) +
    (results?.sermon_notes?.length || 0) +
    (results?.announcements?.length || 0);

  return (
    <div ref={wrapRef} className="relative w-96 max-w-full" data-testid="global-search">
      <div className="relative">
        <Search className="h-4 w-4 absolute left-4 top-1/2 -translate-y-1/2 text-brand-primary/45 pointer-events-none" />
        <input
          type="search"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search people, docs, sermon notes, announcements…"
          data-testid="global-search-input"
          className="w-full h-10 pl-11 pr-10 rounded-full bg-white border border-brand-primary/10 text-sm text-brand-primary placeholder:text-brand-primary/45 shadow-sm focus:outline-none focus:ring-2 focus:ring-terracotta/30"
        />
        {loading && (
          <Loader2 className="h-4 w-4 absolute right-3.5 top-1/2 -translate-y-1/2 animate-spin text-terracotta" />
        )}
      </div>

      {open && debouncedQ.trim().length >= 2 && (
        <div
          className="absolute right-0 mt-2 w-[32rem] max-w-[90vw] bg-white border border-brand-primary/10 rounded-2xl shadow-warm-lg overflow-hidden z-30"
          data-testid="global-search-results"
        >
          {!results || totalHits === 0 ? (
            <div className="p-6 text-center text-sm text-brand-primary/55">
              {loading ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> Searching…
                </span>
              ) : (
                <>
                  <UserX className="h-5 w-5 mx-auto text-brand-primary/35 mb-1" />
                  No matches for "{debouncedQ}"
                </>
              )}
            </div>
          ) : (
            <div className="max-h-[70vh] overflow-y-auto">
              <ResultGroup
                label="People"
                icon={Users}
                rows={results.people?.results || []}
                empty={
                  results.people?.connected === false
                    ? "ChMS not connected — ask an admin"
                    : "No people match"
                }
                dimEmpty={results.people?.connected === false}
                renderRow={(p) => (
                  <>
                    <p className="font-medium text-brand-primary truncate">{p.title || "—"}</p>
                    <p className="text-xs text-brand-primary/60 mt-0.5 flex items-center gap-2 flex-wrap">
                      {p.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{p.email}</span>}
                      {p.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{p.phone}</span>}
                      {!p.email && !p.phone && p.subtitle && <span>{p.subtitle}</span>}
                    </p>
                  </>
                )}
                onPick={(p) => goto(p.href)}
              />
              <ResultGroup
                label="Documents"
                icon={FileText}
                rows={results.documents || []}
                renderRow={(d) => (
                  <>
                    <p className="font-medium text-brand-primary truncate inline-flex items-center gap-1">
                      {d.title}
                      {!d.file_name && d.link_url && <ExternalLink className="h-3 w-3" />}
                    </p>
                    {d.subtitle && <p className="text-xs text-brand-primary/60 mt-0.5 truncate">{d.subtitle}</p>}
                  </>
                )}
                onPick={(d) => goto(d.href)}
              />
              <ResultGroup
                label="Sermon notes"
                icon={BookOpen}
                rows={results.sermon_notes || []}
                renderRow={(s) => (
                  <>
                    <p className="font-medium text-brand-primary truncate">{s.title}</p>
                    <p className="text-xs text-brand-primary/60 mt-0.5">{s.subtitle}</p>
                  </>
                )}
                onPick={(s) => goto(s.href)}
              />
              <ResultGroup
                label="Announcements"
                icon={Megaphone}
                rows={results.announcements || []}
                renderRow={(a) => (
                  <>
                    <p className="font-medium text-brand-primary truncate">{a.title}</p>
                    {a.subtitle && <p className="text-xs text-brand-primary/60 mt-0.5 truncate">{a.subtitle}</p>}
                  </>
                )}
                onPick={(a) => goto(a.href)}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ResultGroup({ label, icon: Icon, rows, renderRow, onPick, empty, dimEmpty }) {
  if ((!rows || rows.length === 0) && !empty) return null;
  return (
    <div className="border-b border-brand-primary/10 last:border-b-0">
      <div className="px-4 pt-3 pb-1 flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-terracotta font-semibold">
        <Icon className="h-3 w-3" /> {label}
      </div>
      {rows && rows.length > 0 ? (
        <ul>
          {rows.map((r, idx) => (
            <li key={r.id ?? idx}>
              <button
                onClick={() => onPick(r)}
                className="w-full text-left px-4 py-2.5 hover:bg-cream/80 transition flex items-start gap-3 focus:outline-none focus:bg-cream/80"
                data-testid={`global-search-hit-${label.toLowerCase().replace(/\s+/g, "-")}-${idx}`}
              >
                <div className="flex-1 min-w-0">{renderRow(r)}</div>
              </button>
            </li>
          ))}
        </ul>
      ) : empty ? (
        <p className={`px-4 py-3 text-xs ${dimEmpty ? "text-brand-primary/40 italic" : "text-brand-primary/55"}`}>
          {empty}
        </p>
      ) : null}
    </div>
  );
}
